import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
} from "@mui/material";
import { ExpandMore, RestartAlt } from "@mui/icons-material";

import { searchMeta } from "@/api";
import { HelpBox, SearchBox } from "@/components/atoms";
import { MetaInterest } from "@/types";
import { useInterestColor } from "@/utils";

interface MetaSearchPanelProps {
  selection: MetaInterest[][];
  activeSelection: number;
  editMode: boolean;
  onSelect: (interest: MetaInterest) => void;
  onError: (error: string | null) => void;
}

const MetaSearchPanel: React.FC<MetaSearchPanelProps> = ({
  selection,
  activeSelection,
  editMode,
  onSelect,
  onError,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getColor } = useInterestColor(selection, activeSelection, editMode);

  const [searchText, setSearchText] = useState<string>("");
  const [searchResults, setSearchResults] = useState<MetaInterest[]>([]);
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (searchText === "") {
      setSearchResults([]);
    } else {
      handleSearch(searchText);
    }
  }, [searchText]);

  const handleSearch = async (searchTerm: string) => {
    const token = await getAccessTokenSilently();
    if (searchTerm.length < 3) return;

    setLoading(true);
    const { data, error } = await searchMeta(token, searchTerm);

    if (data) {
      setSearchResults(data);
      setLoading(false);
      setExpanded(true);
    }

    if (error) {
      onError(error.message);
      setLoading(false);
    }
  };

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleExpand = (event: React.SyntheticEvent) => {
    searchResults.length > 0 && setExpanded(!expanded);
  };

  const handleClearText = () => {
    setSearchText("");
    setSearchResults([]);
    setExpanded(false);
  };

  return (
    <Accordion expanded={expanded} onChange={handleExpand}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchBox value={searchText} onSearch={handleSearchTextChange} />
          <HelpBox title="Clear Text">
            <IconButton onClick={handleClearText}>
              <RestartAlt />
            </IconButton>
          </HelpBox>
          {loading && <CircularProgress size={32} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {searchResults.length > 0 &&
          searchResults.map((interest) => (
            <Chip
              key={`search-${interest.meta_id}`}
              size="small"
              color={getColor(interest.meta_id)}
              label={interest.meta_name}
              onClick={() => onSelect(interest)}
              sx={{ m: 0.5 }}
            />
          ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default MetaSearchPanel;
